/**
 * MeasurementsTab
 * Full measurement scheduling and management for Manager/SID/ASID/Leader/Agent
 * Includes filters, assign/reassign, and reschedule functionality
 *
 * Mobile-first responsive design with card layout on mobile, table on desktop
 */

import React, { useState, useEffect, useMemo } from 'react';
import { measurementAPI } from '../../../services/api';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import { formatSalesTeamLines, formatSidAssignment } from '../../../utils/projectDataUtils';
import { hasDirectorAccess } from '../../../utils/rolePermissions';
import BottomSheet from '../../common/BottomSheet';
import {
  FiGrid,
  FiClock,
  FiCalendar,
  FiUserCheck,
  FiActivity,
  FiCheckCircle,
  FiMapPin,
  FiInbox,
  FiFilter,
  FiChevronUp,
  FiChevronDown,
  FiUsers
} from 'react-icons/fi';

// Responsive hook for mobile detection
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
};

// Status configuration with icons
const statusConfig = {
  pending_schedule: {
    icon: FiClock,
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200',
    label: 'Needs Scheduling'
  },
  scheduled: {
    icon: FiCalendar,
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
    label: 'Unassigned'
  },
  assigned: {
    icon: FiUserCheck,
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200',
    label: 'Assigned'
  },
  in_progress: {
    icon: FiActivity,
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-200',
    label: 'In Progress'
  },
  completed: {
    icon: FiCheckCircle,
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    label: 'Completed'
  },
  cancelled: {
    icon: FiGrid,
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    border: 'border-gray-200',
    label: 'Cancelled'
  }
};

// Enhanced Status Badge with icon
function MeasurementStatusBadge({ status, withIcon = true, size = 'md' }) {
  const config = statusConfig[status] || statusConfig.scheduled;
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-xs px-2.5 py-1 gap-1.5'
  };

  return (
    <span className={`
      inline-flex items-center rounded-full font-medium border
      ${config.bg} ${config.text} ${config.border}
      ${sizeClasses[size]}
    `}>
      {withIcon && <Icon className="w-3 h-3" />}
      {config.label}
    </span>
  );
}

// Mobile Card Component
function MeasurementCard({ measurement, onAssign, onReschedule, onSetSchedule, onTap, canAssignDraughter, formatDateTime }) {
  const salesForm = measurement.designProject?.salesForm;

  // Build location preview
  const locationPreview = useMemo(() => {
    const city = salesForm?.city || '';
    const state = salesForm?.state || '';
    if (city) return `${city}${state ? `, ${state}` : ''}`;
    return measurement.location || 'N/A';
  }, [salesForm, measurement.location]);

  // Get active draughters
  const activeDraughters = (measurement.draughters || [])
    .filter(d => d.isActive !== false)
    .map(d => d.draughter)
    .filter(Boolean);

  const draughtersToShow = activeDraughters.length > 0
    ? activeDraughters
    : (measurement.draughter ? [measurement.draughter] : []);

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md active:bg-gray-50 transition-all duration-200 cursor-pointer"
      onClick={() => onTap(measurement)}
    >
      {/* Row 1: Project # + Status Badge */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-900">
          {measurement.designProject?.projectNumber || 'Pending'}
        </span>
        <MeasurementStatusBadge status={measurement.status} size="sm" />
      </div>

      {/* Row 2: Client Name */}
      <p className="text-sm text-gray-700 mb-3 truncate font-medium">
        {salesForm?.clientName || 'Unknown Client'}
      </p>

      {/* Row 3: Date/Time + Location */}
      <div className="flex flex-col gap-1.5 text-xs text-gray-500 mb-3">
        <span className="flex items-center gap-1.5">
          <FiCalendar className="w-3.5 h-3.5 flex-shrink-0" />
          {measurement.status === 'pending_schedule' ? (
            <span className="text-orange-600 font-medium">Not scheduled</span>
          ) : (
            <span className="text-gray-700">{formatDateTime(measurement.scheduledDate)}</span>
          )}
        </span>
        <span className="flex items-center gap-1.5">
          <FiMapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{locationPreview}</span>
        </span>
      </div>

      {/* Draughter Assignment */}
      {draughtersToShow.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-3 pb-3 border-b border-gray-100">
          <FiUsers className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-medium">
            {draughtersToShow.map(d => d.nickname).join(', ')}
          </span>
          {measurement.isSelfAssigned && draughtersToShow.length === 1 && (
            <span className="text-gray-400">(Self)</span>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
        {measurement.status === 'pending_schedule' && (
          <button
            onClick={() => onSetSchedule(measurement)}
            className="flex-1 min-h-[44px] px-3 py-2.5 bg-orange-50 text-orange-700 rounded-lg text-sm font-medium
                       hover:bg-orange-100 active:bg-orange-200 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
          >
            Set Schedule
          </button>
        )}
        {canAssignDraughter && measurement.status === 'scheduled' && !measurement.assignedDraughterId && (
          <button
            onClick={() => onAssign(measurement)}
            className="flex-1 min-h-[44px] px-3 py-2.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium
                       hover:bg-blue-100 active:bg-blue-200 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Assign
          </button>
        )}
        {canAssignDraughter && measurement.assignedDraughterId && measurement.status !== 'completed' && (
          <button
            onClick={() => onAssign(measurement)}
            className="flex-1 min-h-[44px] px-3 py-2.5 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium
                       hover:bg-purple-100 active:bg-purple-200 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Reassign
          </button>
        )}
        {['scheduled', 'assigned'].includes(measurement.status) && (
          <button
            onClick={() => onReschedule(measurement)}
            className="flex-1 min-h-[44px] px-3 py-2.5 bg-gray-50 text-gray-700 rounded-lg text-sm font-medium
                       hover:bg-gray-100 active:bg-gray-200 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Reschedule
          </button>
        )}
      </div>
    </div>
  );
}

// Mobile Card Skeleton
function MeasurementCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
      <div className="flex items-center justify-between mb-2">
        <div className="h-4 bg-gray-200 rounded w-24" />
        <div className="h-5 bg-gray-200 rounded-full w-24" />
      </div>
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
      <div className="space-y-1.5 mb-3">
        <div className="h-3 bg-gray-200 rounded w-32" />
        <div className="h-3 bg-gray-200 rounded w-40" />
      </div>
      <div className="h-11 bg-gray-200 rounded-lg" />
    </div>
  );
}

// Empty State Component
function MeasurementEmptyState({ filter, hasFilters }) {
  const configs = {
    all: {
      icon: FiInbox,
      title: 'No measurements',
      description: 'Measurements will appear here when projects require site visits.'
    },
    pending_schedule: {
      icon: FiClock,
      title: 'All caught up!',
      description: 'No measurements pending scheduling. Great work!'
    },
    unassigned: {
      icon: FiUserCheck,
      title: 'All assigned',
      description: 'All scheduled measurements have been assigned to draughters.'
    },
    assigned: {
      icon: FiUsers,
      title: 'No assigned measurements',
      description: 'Measurements will appear here once assigned to draughters.'
    },
    in_progress: {
      icon: FiActivity,
      title: 'No measurements in progress',
      description: 'In-progress measurements will appear here.'
    },
    completed: {
      icon: FiCheckCircle,
      title: 'No completed measurements',
      description: 'Completed measurements will appear here.'
    }
  };

  const config = hasFilters
    ? { icon: FiFilter, title: 'No results', description: 'No measurements match the selected filters. Try adjusting your filters.' }
    : configs[filter] || configs.all;

  const Icon = config.icon;

  return (
    <div className="text-center py-16 px-4">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
        <Icon className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-1">{config.title}</h3>
      <p className="text-sm text-gray-500 max-w-sm mx-auto">{config.description}</p>
    </div>
  );
}

export default function MeasurementsTab() {
  const { user: currentUser } = useAuth();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const [measurements, setMeasurements] = useState([]);
  const [draughters, setDraughters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [measurementToReschedule, setMeasurementToReschedule] = useState(null);
  const [setScheduleModalOpen, setSetScheduleModalOpen] = useState(false);
  const [measurementToSchedule, setMeasurementToSchedule] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [measurementToView, setMeasurementToView] = useState(null);

  // Additional filters
  const [draughterFilter, setDraughterFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateSort, setDateSort] = useState('asc');
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  // Roles that can assign draughters
  const canAssignDraughter = hasDirectorAccess(currentUser?.role) || ['manager', 'leader', 'sid', 'asid', 'operation_head'].includes(currentUser?.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filter === 'pending_schedule') {
        filters.status = 'pending_schedule';
      } else if (filter === 'unassigned') {
        filters.status = 'scheduled';
      } else if (filter === 'assigned') {
        filters.status = 'assigned';
      } else if (filter === 'in_progress') {
        filters.status = 'in_progress';
      } else if (filter === 'completed') {
        filters.status = 'completed';
      }

      // Only fetch draughters if user can assign them
      if (canAssignDraughter) {
        const [measurementsRes, draughtersRes] = await Promise.all([
          measurementAPI.getAll(filters),
          api.get('/users', { params: { role: 'draughter_site' } })
        ]);
        setMeasurements(measurementsRes.data.data || []);
        setDraughters(draughtersRes.data.data || draughtersRes.data.users || []);
      } else {
        // Agents and Leaders only fetch measurements
        const measurementsRes = await measurementAPI.getAll(filters);
        setMeasurements(measurementsRes.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load measurement schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (measurementId, draughterIds) => {
    try {
      // draughterIds is now an array
      await measurementAPI.assign(measurementId, draughterIds);
      const message = draughterIds.length > 1 ? 'Draughters assigned successfully' : 'Draughter assigned successfully';
      toast.success(message);
      fetchData();
      setAssignModalOpen(false);
      setSelectedMeasurement(null);
    } catch (err) {
      console.error('Error assigning draughter:', err);
      toast.error(err.message || 'Failed to assign draughter');
    }
  };

  const openAssignModal = (measurement) => {
    setSelectedMeasurement(measurement);
    setAssignModalOpen(true);
  };

  const openRescheduleModal = (measurement) => {
    setMeasurementToReschedule(measurement);
    setRescheduleModalOpen(true);
  };

  const handleReschedule = async (measurementId, newScheduledDate) => {
    try {
      await measurementAPI.reschedule(measurementId, newScheduledDate);
      toast.success('Measurement rescheduled successfully');
      fetchData();
      setRescheduleModalOpen(false);
      setMeasurementToReschedule(null);
    } catch (err) {
      console.error('Error rescheduling measurement:', err);
      toast.error(err.message || 'Failed to reschedule measurement');
    }
  };

  const openSetScheduleModal = (measurement) => {
    setMeasurementToSchedule(measurement);
    setSetScheduleModalOpen(true);
  };

  const openDetailModal = (measurement) => {
    setMeasurementToView(measurement);
    setDetailModalOpen(true);
  };

  const handleSetSchedule = async (measurementId, scheduledDate) => {
    try {
      await measurementAPI.setSchedule(measurementId, scheduledDate);
      toast.success('Measurement scheduled successfully');
      fetchData();
      setSetScheduleModalOpen(false);
      setMeasurementToSchedule(null);
    } catch (err) {
      console.error('Error setting schedule:', err);
      toast.error(err.message || 'Failed to set schedule');
    }
  };

  // Filter and sort measurements
  const getFilteredAndSortedMeasurements = () => {
    let filtered = [...measurements];

    // Filter by draughter
    if (draughterFilter) {
      filtered = filtered.filter(m => {
        const activeDraughters = (m.draughters || [])
          .filter(d => d.isActive !== false)
          .map(d => d.draughterId || d.draughter?.id);
        return activeDraughters.includes(draughterFilter) || m.assignedDraughterId === draughterFilter;
      });
    }

    // Filter by date range
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(m => {
        if (!m.scheduledDate) return true; // Keep not scheduled items
        return new Date(m.scheduledDate) >= fromDate;
      });
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(m => {
        if (!m.scheduledDate) return true; // Keep not scheduled items
        return new Date(m.scheduledDate) <= toDate;
      });
    }

    // Sort by date - not scheduled (null dates) always at top
    filtered.sort((a, b) => {
      const aDate = a.scheduledDate ? new Date(a.scheduledDate) : null;
      const bDate = b.scheduledDate ? new Date(b.scheduledDate) : null;

      // Not scheduled items always at top
      if (!aDate && !bDate) return 0;
      if (!aDate) return -1;
      if (!bDate) return 1;

      // Sort by date
      if (dateSort === 'asc') {
        return aDate - bDate;
      } else {
        return bDate - aDate;
      }
    });

    return filtered;
  };

  const filteredMeasurements = getFilteredAndSortedMeasurements();

  const toggleDateSort = () => {
    setDateSort(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
    setShowDateRangePicker(false);
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: measurements.length,
    pendingSchedule: measurements.filter(m => m.status === 'pending_schedule').length,
    unassigned: measurements.filter(m => m.status === 'scheduled' && !m.assignedDraughterId).length,
    assigned: measurements.filter(m => m.status === 'assigned').length,
    inProgress: measurements.filter(m => m.status === 'in_progress').length,
    completed: measurements.filter(m => m.status === 'completed').length
  };

  // Stats card configuration
  const statsCards = [
    { key: 'all', label: 'Total', count: stats.total, icon: FiGrid, bgLight: 'bg-gray-50', bgActive: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
    { key: 'pending_schedule', label: 'Needs Scheduling', count: stats.pendingSchedule, icon: FiClock, bgLight: 'bg-orange-50', bgActive: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
    { key: 'unassigned', label: 'Unassigned', count: stats.unassigned, icon: FiCalendar, bgLight: 'bg-yellow-50', bgActive: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
    { key: 'assigned', label: 'Assigned', count: stats.assigned, icon: FiUserCheck, bgLight: 'bg-blue-50', bgActive: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    { key: 'in_progress', label: 'In Progress', count: stats.inProgress, icon: FiActivity, bgLight: 'bg-purple-50', bgActive: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
    { key: 'completed', label: 'Completed', count: stats.completed, icon: FiCheckCircle, bgLight: 'bg-green-50', bgActive: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' }
  ];

  return (
    <div className="space-y-6">
      {/* Quick Stats - Clickable cards with icons */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {statsCards.map((card) => {
          const Icon = card.icon;
          const isActive = filter === card.key;

          return (
            <button
              key={card.key}
              onClick={() => setFilter(card.key)}
              className={`
                p-3 md:p-4 rounded-xl border transition-all duration-200 text-left
                min-h-[88px] flex flex-col justify-between
                focus:outline-none focus:ring-2 focus:ring-offset-2
                ${isActive
                  ? `${card.bgActive} ${card.border} ring-2 ring-offset-1 shadow-sm`
                  : `${card.bgLight} ${card.border} hover:shadow-md hover:scale-[1.02]`
                }
                ${card.key === 'all' ? 'focus:ring-gray-400' : ''}
                ${card.key === 'pending_schedule' ? 'focus:ring-orange-400' : ''}
                ${card.key === 'unassigned' ? 'focus:ring-yellow-400' : ''}
                ${card.key === 'assigned' ? 'focus:ring-blue-400' : ''}
                ${card.key === 'in_progress' ? 'focus:ring-purple-400' : ''}
                ${card.key === 'completed' ? 'focus:ring-green-400' : ''}
              `}
              style={isActive ? {
                '--tw-ring-color': card.key === 'all' ? '#9ca3af' :
                  card.key === 'pending_schedule' ? '#fb923c' :
                  card.key === 'unassigned' ? '#fbbf24' :
                  card.key === 'assigned' ? '#60a5fa' :
                  card.key === 'in_progress' ? '#a78bfa' : '#4ade80'
              } : {}}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${card.text}`} />
                {isActive && (
                  <span className={`w-2 h-2 rounded-full ${
                    card.key === 'all' ? 'bg-gray-500' :
                    card.key === 'pending_schedule' ? 'bg-orange-500' :
                    card.key === 'unassigned' ? 'bg-yellow-500' :
                    card.key === 'assigned' ? 'bg-blue-500' :
                    card.key === 'in_progress' ? 'bg-purple-500' : 'bg-green-500'
                  }`} />
                )}
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{card.count}</p>
                <p className={`text-xs font-medium ${card.text}`}>{card.label}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter Pills - Mobile: horizontal scroll, Desktop: flex wrap */}
      <div className={`${isMobile ? 'overflow-x-auto scrollbar-hide -mx-4 px-4' : ''}`}>
        <div className={`flex gap-2 ${isMobile ? 'snap-x snap-mandatory min-w-max py-1' : 'flex-wrap'}`}>
          {[
            { key: 'all', label: 'All', count: stats.total, activeColor: 'bg-gray-700' },
            { key: 'pending_schedule', label: 'Needs Scheduling', count: stats.pendingSchedule, activeColor: 'bg-orange-600' },
            { key: 'unassigned', label: 'Unassigned', count: stats.unassigned, activeColor: 'bg-yellow-600' },
            { key: 'assigned', label: 'Assigned', count: stats.assigned, activeColor: 'bg-blue-600' },
            { key: 'in_progress', label: 'In Progress', count: stats.inProgress, activeColor: 'bg-purple-600' },
            { key: 'completed', label: 'Completed', count: stats.completed, activeColor: 'bg-green-600' }
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => setFilter(item.key)}
              className={`
                ${isMobile ? 'snap-start flex-shrink-0' : ''}
                px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap
                min-h-[44px] transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-offset-2
                ${filter === item.key
                  ? `${item.activeColor} text-white shadow-md`
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                }
                ${item.key === 'all' ? 'focus:ring-gray-400' : ''}
                ${item.key === 'pending_schedule' ? 'focus:ring-orange-400' : ''}
                ${item.key === 'unassigned' ? 'focus:ring-yellow-400' : ''}
                ${item.key === 'assigned' ? 'focus:ring-blue-400' : ''}
                ${item.key === 'in_progress' ? 'focus:ring-purple-400' : ''}
                ${item.key === 'completed' ? 'focus:ring-green-400' : ''}
              `}
            >
              {item.label} ({item.count})
            </button>
          ))}
        </div>
      </div>

      {/* Additional Filters */}
      <div className="flex gap-4 flex-wrap mb-6 items-end">
        {/* Draughter Filter */}
        {canAssignDraughter && draughters.length > 0 && (
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">Site Draughter</label>
            <select
              value={draughterFilter}
              onChange={(e) => setDraughterFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px]"
            >
              <option value="">All Draughters</option>
              {draughters.map(d => (
                <option key={d.id} value={d.id}>{d.nickname}</option>
              ))}
            </select>
          </div>
        )}

        {/* Date Range Filter */}
        <div className="flex flex-col relative">
          <label className="text-xs font-medium text-gray-500 mb-1">Date Range</label>
          <button
            onClick={() => setShowDateRangePicker(!showDateRangePicker)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 flex items-center gap-2 min-w-[220px]"
          >
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-gray-700">
              {dateFrom && dateTo
                ? `${formatDateShort(dateFrom)} - ${formatDateShort(dateTo)}`
                : dateFrom
                  ? `${formatDateShort(dateFrom)} - Select end`
                  : 'Select dates'}
            </span>
            {(dateFrom || dateTo) && (
              <span
                onClick={(e) => { e.stopPropagation(); clearDateRange(); }}
                className="ml-auto text-gray-400 hover:text-gray-600"
              >
                ×
              </span>
            )}
          </button>

          {/* Date Range Calendar Dropdown */}
          {showDateRangePicker && (
            <DateRangeCalendar
              dateFrom={dateFrom}
              dateTo={dateTo}
              onSelect={(from, to) => {
                setDateFrom(from);
                setDateTo(to);
              }}
              onClear={clearDateRange}
              onClose={() => setShowDateRangePicker(false)}
            />
          )}
        </div>

        {/* Filter summary */}
        {(draughterFilter || dateFrom || dateTo) && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Showing {filteredMeasurements.length} of {measurements.length}</span>
            <button
              onClick={() => { setDraughterFilter(''); clearDateRange(); }}
              className="text-blue-600 hover:text-blue-800"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Content: Loading / Empty / Table (desktop) / Cards (mobile) */}
      {loading ? (
        // Loading State
        isMobile ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <MeasurementCardSkeleton key={i} />)}
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
            <div className="animate-pulse">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex gap-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                    <div key={i} className="h-4 bg-gray-200 rounded w-20" />
                  ))}
                </div>
              </div>
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="px-4 py-4 border-b border-gray-100">
                  <div className="flex gap-4 items-center">
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-5 bg-gray-200 rounded-full w-20" />
                    <div className="h-4 bg-gray-200 rounded w-32" />
                    <div className="h-4 bg-gray-200 rounded w-40" />
                    <div className="h-4 bg-gray-200 rounded w-20" />
                    <div className="h-4 bg-gray-200 rounded w-24" />
                    <div className="h-4 bg-gray-200 rounded w-20" />
                    <div className="h-4 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ) : filteredMeasurements.length === 0 ? (
        // Empty State
        <MeasurementEmptyState
          filter={filter}
          hasFilters={!!draughterFilter || !!dateFrom || !!dateTo}
        />
      ) : isMobile ? (
        // Mobile Card Layout
        <div className="space-y-4">
          {filteredMeasurements.map((measurement) => (
            <MeasurementCard
              key={measurement.id}
              measurement={measurement}
              onAssign={openAssignModal}
              onReschedule={openRescheduleModal}
              onSetSchedule={openSetScheduleModal}
              onTap={openDetailModal}
              canAssignDraughter={canAssignDraughter}
              formatDateTime={formatDateTime}
            />
          ))}
        </div>
      ) : (
        // Desktop Table Layout
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th
                  onClick={toggleDateSort}
                  className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors group"
                >
                  <div className="flex items-center gap-1.5">
                    Date & Time
                    <span className="text-gray-400 group-hover:text-gray-600 transition-colors">
                      {dateSort === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                    </span>
                  </div>
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" style={{ minWidth: '180px' }}>
                  Location
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Property
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" style={{ minWidth: '140px' }}>
                  Sales Team
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  SID Assigned
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Draughter
                </th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMeasurements.map((measurement) => {
                // Build compact address (3 lines max)
                const salesForm = measurement.designProject?.salesForm;
                const addressLine1 = salesForm?.addressLine1 || '';
                const addressLine2 = salesForm?.addressLine2 || '';
                const postcode = salesForm?.postcode || '';
                const city = salesForm?.city || '';
                const state = salesForm?.state || '';

                // Line 1: addressLine1
                // Line 2: addressLine2 (if exists) or city + postcode
                // Line 3: state (if not already shown)
                const locationLine1 = addressLine1;
                const locationLine2 = addressLine2 || `${postcode} ${city}`.trim();
                const locationLine3 = addressLine2 ? `${postcode} ${city}, ${state}`.trim() : state;

                return (
                <tr key={measurement.id} className="hover:bg-gray-50 transition-colors group">
                  {/* Project */}
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {measurement.designProject?.projectNumber}
                    </div>
                    <div className="text-xs text-gray-500">
                      {salesForm?.clientName}
                    </div>
                  </td>
                  {/* Status */}
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <MeasurementStatusBadge status={measurement.status} />
                  </td>
                  {/* Date & Time */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {measurement.status === 'pending_schedule' ? (
                        <span className="text-orange-600 font-medium">Not scheduled</span>
                      ) : (
                        formatDateTime(measurement.scheduledDate)
                      )}
                    </div>
                  </td>
                  {/* Location */}
                  <td className="px-4 py-3" style={{ maxWidth: '200px' }}>
                    <div className="text-xs text-gray-700 leading-tight">
                      {locationLine1 && <div className="truncate" title={locationLine1}>{locationLine1}</div>}
                      {locationLine2 && <div className="truncate text-gray-500" title={locationLine2}>{locationLine2}</div>}
                      {locationLine3 && locationLine3 !== state && <div className="truncate text-gray-500" title={locationLine3}>{locationLine3}</div>}
                      {!locationLine1 && !locationLine2 && (
                        <div className="truncate" title={measurement.location}>{measurement.location || 'N/A'}</div>
                      )}
                    </div>
                  </td>
                  {/* Property */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{measurement.propertyType || 'N/A'}</div>
                    {measurement.propertySize && (
                      <div className="text-xs text-gray-500">{measurement.propertySize} sqft</div>
                    )}
                  </td>
                  {/* Sales Team (Agent + Leader) */}
                  <td className="px-4 py-3" style={{ minWidth: '140px' }}>
                    <div className="text-xs text-gray-900 leading-tight">
                      {(() => {
                        const lines = formatSalesTeamLines(
                          measurement.designProject?.salesAgents,
                          measurement.designProject?.salesForm
                        );
                        if (lines.length === 0) return <div>N/A</div>;
                        return lines.map((line, idx) => (
                          <div key={idx}>{line}</div>
                        ));
                      })()}
                    </div>
                  </td>
                  {/* SID Assigned */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatSidAssignment(measurement.designProject?.teamMembers)}
                    </div>
                  </td>
                  {/* Draughter */}
                  <td className="px-4 py-3">
                    {(() => {
                      // Get all active draughters from the draughters array
                      const activeDraughters = (measurement.draughters || [])
                        .filter(d => d.isActive !== false)
                        .map(d => d.draughter)
                        .filter(Boolean);

                      // Fallback to legacy single draughter if no draughters array
                      const draughtersToShow = activeDraughters.length > 0
                        ? activeDraughters
                        : (measurement.draughter ? [measurement.draughter] : []);

                      if (draughtersToShow.length === 0) {
                        return <span className="text-sm text-gray-400">Unassigned</span>;
                      }

                      return (
                        <div className="text-sm text-gray-900">
                          {draughtersToShow.map(d => d.nickname).join(', ')}
                          {measurement.isSelfAssigned && draughtersToShow.length === 1 && (
                            <span className="text-xs text-gray-500 ml-1">(Self)</span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  {/* Actions */}
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {/* Set Schedule button for pending_schedule measurements */}
                      {measurement.status === 'pending_schedule' && (
                        <button
                          onClick={() => openSetScheduleModal(measurement)}
                          className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium
                                     hover:bg-orange-100 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400"
                        >
                          Set Schedule
                        </button>
                      )}
                      {/* Assign button - only for scheduled measurements */}
                      {canAssignDraughter && measurement.status === 'scheduled' && !measurement.assignedDraughterId && (
                        <button
                          onClick={() => openAssignModal(measurement)}
                          className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium
                                     hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          Assign
                        </button>
                      )}
                      {canAssignDraughter && measurement.assignedDraughterId && measurement.status !== 'completed' && (
                        <button
                          onClick={() => openAssignModal(measurement)}
                          className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium
                                     hover:bg-purple-100 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400"
                        >
                          Reassign
                        </button>
                      )}
                      {['scheduled', 'assigned'].includes(measurement.status) && (
                        <button
                          onClick={() => openRescheduleModal(measurement)}
                          className="px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg text-xs font-medium
                                     hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
                        >
                          Reschedule
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign Modal - BottomSheet on mobile, Modal on desktop */}
      {assignModalOpen && selectedMeasurement && (
        isMobile ? (
          <BottomSheet
            isOpen={assignModalOpen}
            onClose={() => {
              setAssignModalOpen(false);
              setSelectedMeasurement(null);
            }}
            title="Assign Site Draughter(s)"
            maxHeight="85vh"
          >
            <AssignDraughterContent
              measurement={selectedMeasurement}
              draughters={draughters}
              onAssign={handleAssign}
              onClose={() => {
                setAssignModalOpen(false);
                setSelectedMeasurement(null);
              }}
              formatDateTime={formatDateTime}
              isMobile={true}
            />
          </BottomSheet>
        ) : (
          <AssignDraughterModal
            measurement={selectedMeasurement}
            draughters={draughters}
            onAssign={handleAssign}
            onClose={() => {
              setAssignModalOpen(false);
              setSelectedMeasurement(null);
            }}
            formatDateTime={formatDateTime}
          />
        )
      )}

      {/* Reschedule Modal - BottomSheet on mobile, Modal on desktop */}
      {rescheduleModalOpen && measurementToReschedule && (
        isMobile ? (
          <BottomSheet
            isOpen={rescheduleModalOpen}
            onClose={() => {
              setRescheduleModalOpen(false);
              setMeasurementToReschedule(null);
            }}
            title="Reschedule Measurement"
            maxHeight="85vh"
          >
            <RescheduleContent
              measurement={measurementToReschedule}
              onReschedule={handleReschedule}
              onClose={() => {
                setRescheduleModalOpen(false);
                setMeasurementToReschedule(null);
              }}
              formatDateTime={formatDateTime}
              isMobile={true}
            />
          </BottomSheet>
        ) : (
          <RescheduleMeasurementModal
            measurement={measurementToReschedule}
            onReschedule={handleReschedule}
            onClose={() => {
              setRescheduleModalOpen(false);
              setMeasurementToReschedule(null);
            }}
            formatDateTime={formatDateTime}
          />
        )
      )}

      {/* Set Schedule Modal - BottomSheet on mobile, Modal on desktop */}
      {setScheduleModalOpen && measurementToSchedule && (
        isMobile ? (
          <BottomSheet
            isOpen={setScheduleModalOpen}
            onClose={() => {
              setSetScheduleModalOpen(false);
              setMeasurementToSchedule(null);
            }}
            title="Set Measurement Schedule"
            maxHeight="85vh"
          >
            <SetScheduleContent
              measurement={measurementToSchedule}
              onSetSchedule={handleSetSchedule}
              onClose={() => {
                setSetScheduleModalOpen(false);
                setMeasurementToSchedule(null);
              }}
              isMobile={true}
            />
          </BottomSheet>
        ) : (
          <SetScheduleModal
            measurement={measurementToSchedule}
            onSetSchedule={handleSetSchedule}
            onClose={() => {
              setSetScheduleModalOpen(false);
              setMeasurementToSchedule(null);
            }}
          />
        )
      )}

      {/* Detail View BottomSheet - Mobile only */}
      {detailModalOpen && measurementToView && (
        <BottomSheet
          isOpen={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setMeasurementToView(null);
          }}
          title="Measurement Details"
          maxHeight="90vh"
        >
          <MeasurementDetailContent
            measurement={measurementToView}
            onAssign={openAssignModal}
            onReschedule={openRescheduleModal}
            onSetSchedule={openSetScheduleModal}
            onClose={() => {
              setDetailModalOpen(false);
              setMeasurementToView(null);
            }}
            canAssignDraughter={canAssignDraughter}
            formatDateTime={formatDateTime}
          />
        </BottomSheet>
      )}
    </div>
  );
}

// Mobile Measurement Detail Content for BottomSheet
function MeasurementDetailContent({ measurement, onAssign, onReschedule, onSetSchedule, onClose, canAssignDraughter, formatDateTime }) {
  const salesForm = measurement.designProject?.salesForm;

  // Build full address
  const addressLine1 = salesForm?.addressLine1 || '';
  const addressLine2 = salesForm?.addressLine2 || '';
  const postcode = salesForm?.postcode || '';
  const city = salesForm?.city || '';
  const state = salesForm?.state || '';

  const fullAddress = [
    addressLine1,
    addressLine2,
    `${postcode} ${city}`.trim(),
    state
  ].filter(Boolean).join('\n');

  // Get active draughters
  const activeDraughters = (measurement.draughters || [])
    .filter(d => d.isActive !== false)
    .map(d => d.draughter)
    .filter(Boolean);

  const draughtersToShow = activeDraughters.length > 0
    ? activeDraughters
    : (measurement.draughter ? [measurement.draughter] : []);

  // Sales team info
  const salesTeamLines = formatSalesTeamLines(
    measurement.designProject?.salesAgents,
    salesForm
  );

  // SID assigned
  const sidAssigned = formatSidAssignment(measurement.designProject?.teamMembers);

  return (
    <div className="px-4 pb-6 pt-2">
      {/* Header: Project # + Status */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-lg font-bold text-gray-900">
          {measurement.designProject?.projectNumber || 'Pending'}
        </span>
        <MeasurementStatusBadge status={measurement.status} />
      </div>

      {/* Client Name */}
      <div className="mb-4 pb-4 border-b border-gray-100">
        <p className="text-base font-medium text-gray-900">
          {salesForm?.clientName || 'Unknown Client'}
        </p>
      </div>

      {/* Info Grid */}
      <div className="space-y-4 mb-6">
        {/* Date & Time */}
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <FiCalendar className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date & Time</p>
            <p className="text-sm text-gray-900 font-medium">
              {measurement.status === 'pending_schedule' ? (
                <span className="text-orange-600">Not scheduled</span>
              ) : (
                formatDateTime(measurement.scheduledDate)
              )}
            </p>
          </div>
        </div>

        {/* Location */}
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
            <FiMapPin className="w-4 h-4 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location</p>
            <p className="text-sm text-gray-900 whitespace-pre-line">
              {fullAddress || measurement.location || 'N/A'}
            </p>
          </div>
        </div>

        {/* Property */}
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
            <FiGrid className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Property</p>
            <p className="text-sm text-gray-900">
              {measurement.propertyType || 'N/A'}
              {measurement.propertySize && (
                <span className="text-gray-500 ml-1">({measurement.propertySize} sqft)</span>
              )}
            </p>
          </div>
        </div>

        {/* Sales Team */}
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center">
            <FiUsers className="w-4 h-4 text-yellow-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sales Team</p>
            {salesTeamLines.length > 0 ? (
              <div className="text-sm text-gray-900">
                {salesTeamLines.map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">N/A</p>
            )}
          </div>
        </div>

        {/* SID Assigned */}
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
            <FiUserCheck className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">SID Assigned</p>
            <p className="text-sm text-gray-900">{sidAssigned || 'N/A'}</p>
          </div>
        </div>

        {/* Draughter */}
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
            <FiActivity className="w-4 h-4 text-pink-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Draughter</p>
            {draughtersToShow.length > 0 ? (
              <p className="text-sm text-gray-900">
                {draughtersToShow.map(d => d.nickname).join(', ')}
                {measurement.isSelfAssigned && draughtersToShow.length === 1 && (
                  <span className="text-gray-500 ml-1">(Self)</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-gray-500">Unassigned</p>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-2">
        {measurement.status === 'pending_schedule' && (
          <button
            onClick={() => { onClose(); onSetSchedule(measurement); }}
            className="w-full min-h-[48px] px-4 py-3 bg-orange-50 text-orange-700 rounded-xl text-sm font-medium
                       hover:bg-orange-100 active:bg-orange-200 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
          >
            Set Schedule
          </button>
        )}
        {canAssignDraughter && measurement.status === 'scheduled' && !measurement.assignedDraughterId && (
          <button
            onClick={() => { onClose(); onAssign(measurement); }}
            className="w-full min-h-[48px] px-4 py-3 bg-blue-50 text-blue-700 rounded-xl text-sm font-medium
                       hover:bg-blue-100 active:bg-blue-200 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Assign Draughter
          </button>
        )}
        {canAssignDraughter && measurement.assignedDraughterId && measurement.status !== 'completed' && (
          <button
            onClick={() => { onClose(); onAssign(measurement); }}
            className="w-full min-h-[48px] px-4 py-3 bg-purple-50 text-purple-700 rounded-xl text-sm font-medium
                       hover:bg-purple-100 active:bg-purple-200 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Reassign Draughter
          </button>
        )}
        {['scheduled', 'assigned'].includes(measurement.status) && (
          <button
            onClick={() => { onClose(); onReschedule(measurement); }}
            className="w-full min-h-[48px] px-4 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium
                       hover:bg-gray-200 active:bg-gray-300 transition-colors
                       focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Reschedule
          </button>
        )}
        <button
          onClick={onClose}
          className="w-full min-h-[48px] px-4 py-3 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium
                     hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// Mobile-optimized Assign Draughter Content for BottomSheet
function AssignDraughterContent({ measurement, draughters, onAssign, onClose, formatDateTime, isMobile }) {
  const currentDraughterIds = (measurement.draughters || [])
    .filter(d => d.isActive !== false)
    .map(d => d.draughterId || d.draughter?.id);

  const initialSelected = currentDraughterIds.length > 0
    ? currentDraughterIds
    : (measurement.assignedDraughterId ? [measurement.assignedDraughterId] : []);

  const [selectedDraughters, setSelectedDraughters] = useState(initialSelected);

  const handleToggleDraughter = (draughterId) => {
    setSelectedDraughters(prev => {
      if (prev.includes(draughterId)) {
        return prev.filter(id => id !== draughterId);
      } else {
        return [...prev, draughterId];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedDraughters.length === 0) {
      toast.error('Please select at least one draughter');
      return;
    }
    onAssign(measurement.id, selectedDraughters);
  };

  const hasExistingAssignment = currentDraughterIds.length > 0 || measurement.assignedDraughterId;

  return (
    <div className="px-4 pb-6 pt-2">
      {/* Project Info */}
      <div className="mb-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
        <p className="text-sm text-gray-600 mb-1.5">
          <span className="font-medium">Project:</span>{' '}
          <span className="font-semibold text-gray-900">{measurement.designProject?.projectNumber}</span>
        </p>
        <p className="text-sm text-gray-600 mb-1.5">
          <span className="font-medium">Client:</span>{' '}
          {measurement.designProject?.salesForm?.clientName}
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Scheduled:</span>{' '}
          <span className="font-semibold text-gray-900">{formatDateTime(measurement.scheduledDate)}</span>
        </p>
      </div>

      {hasExistingAssignment && (
        <div className="mb-4 bg-yellow-50 rounded-xl p-3 border border-yellow-200">
          <p className="text-sm text-yellow-800">
            Currently assigned to:{' '}
            <span className="font-semibold">
              {(measurement.draughters || [])
                .filter(d => d.isActive !== false)
                .map(d => d.draughter?.nickname)
                .filter(Boolean)
                .join(', ') || measurement.draughter?.nickname || 'Unknown'}
            </span>
          </p>
        </div>
      )}

      {/* Draughter Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Draughter(s)
        </label>
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          {draughters.map((draughter, idx) => (
            <label
              key={draughter.id}
              className={`
                flex items-center px-4 py-3.5 cursor-pointer
                min-h-[52px] transition-colors
                ${idx !== draughters.length - 1 ? 'border-b border-gray-100' : ''}
                ${selectedDraughters.includes(draughter.id)
                  ? 'bg-blue-50'
                  : 'hover:bg-gray-50 active:bg-gray-100'
                }
              `}
            >
              <input
                type="checkbox"
                checked={selectedDraughters.includes(draughter.id)}
                onChange={() => handleToggleDraughter(draughter.id)}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-3 text-sm text-gray-900 font-medium">
                {draughter.nickname}
              </span>
              <span className="ml-1 text-sm text-gray-500">
                - {draughter.fullName}
              </span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {selectedDraughters.length} selected
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 min-h-[48px] px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium
                     hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={selectedDraughters.length === 0}
          className="flex-1 min-h-[48px] px-4 py-3 bg-blue-600 text-white rounded-xl font-medium
                     hover:bg-blue-700 active:bg-blue-800 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {hasExistingAssignment ? 'Reassign' : 'Assign'}
        </button>
      </div>
    </div>
  );
}

// Mobile-optimized Reschedule Content for BottomSheet
function RescheduleContent({ measurement, onReschedule, onClose, formatDateTime, isMobile }) {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (measurement.scheduledDate) {
      const date = new Date(measurement.scheduledDate);
      setNewDate(date.toISOString().split('T')[0]);
      setNewTime(date.toTimeString().slice(0, 5));
    }
  }, [measurement]);

  const getMinDate = () => new Date().toISOString().split('T')[0];

  const getQuickDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0];
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 18 && minute > 0) break;
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const label = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ label, value: time24 });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleSubmit = async () => {
    if (!newDate || !newTime) {
      toast.error('Please select both date and time');
      return;
    }
    setSubmitting(true);
    try {
      const scheduledDateTime = new Date(`${newDate}T${newTime}`);
      await onReschedule(measurement.id, scheduledDateTime.toISOString());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 pb-6 pt-2">
      {/* Project Info */}
      <div className="mb-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
        <p className="text-sm text-gray-600 mb-1.5">
          <span className="font-medium">Project:</span>{' '}
          <span className="font-semibold text-gray-900">{measurement.designProject?.projectNumber}</span>
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Current:</span>{' '}
          <span className="font-semibold text-orange-600">{formatDateTime(measurement.scheduledDate)}</span>
        </p>
      </div>

      {/* Date Selection */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
          Choose New Date
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { days: 0, label: 'Today' },
            { days: 1, label: 'Tomorrow' },
            { days: 2, label: '+2 Days' },
            { days: 7, label: '+1 Week' }
          ].map((opt) => (
            <button
              key={opt.days}
              onClick={() => setNewDate(getQuickDate(opt.days))}
              disabled={submitting}
              className={`
                px-4 py-2.5 rounded-xl font-medium text-sm min-h-[44px]
                transition-all disabled:opacity-50
                ${newDate === getQuickDate(opt.days)
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-orange-400'
                }
              `}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          min={getMinDate()}
          disabled={submitting}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm
                     focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[48px]"
        />
      </div>

      {/* Time Selection */}
      {newDate && (
        <div className="mb-6">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Choose New Time
          </label>
          <div className="grid grid-cols-4 gap-2">
            {timeSlots.map((slot) => (
              <button
                key={slot.value}
                onClick={() => setNewTime(slot.value)}
                disabled={submitting}
                className={`
                  px-2 py-3 rounded-xl font-medium text-xs min-h-[48px]
                  transition-all disabled:opacity-50
                  ${newTime === slot.value
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-white border border-gray-300 text-gray-700 hover:border-orange-400'
                  }
                `}
              >
                {slot.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onClose}
          disabled={submitting}
          className="flex-1 min-h-[48px] px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium
                     hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || !newDate || !newTime}
          className="flex-1 min-h-[48px] px-4 py-3 bg-orange-600 text-white rounded-xl font-medium
                     hover:bg-orange-700 active:bg-orange-800 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Reschedule
        </button>
      </div>
    </div>
  );
}

// Mobile-optimized Set Schedule Content for BottomSheet
function SetScheduleContent({ measurement, onSetSchedule, onClose, isMobile }) {
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getMinDate = () => new Date().toISOString().split('T')[0];

  const getQuickDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0];
  };

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 18 && minute > 0) break;
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const label = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ label, value: time24 });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleSubmit = async () => {
    if (!scheduledDate || !scheduledTime) {
      toast.error('Please select both date and time');
      return;
    }
    setSubmitting(true);
    try {
      const dateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      await onSetSchedule(measurement.id, dateTime.toISOString());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 pb-6 pt-2">
      {/* Project Info */}
      <div className="mb-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
        <p className="text-sm text-gray-600 mb-1.5">
          <span className="font-medium">Project:</span>{' '}
          <span className="font-semibold text-gray-900">{measurement.designProject?.projectNumber}</span>
        </p>
        <p className="text-sm text-gray-600">
          <span className="font-medium">Client:</span>{' '}
          {measurement.designProject?.salesForm?.clientName}
        </p>
      </div>

      {/* Date Selection */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
          Choose Date
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            { days: 0, label: 'Today' },
            { days: 1, label: 'Tomorrow' },
            { days: 2, label: '+2 Days' },
            { days: 7, label: '+1 Week' }
          ].map((opt) => (
            <button
              key={opt.days}
              onClick={() => setScheduledDate(getQuickDate(opt.days))}
              disabled={submitting}
              className={`
                px-4 py-2.5 rounded-xl font-medium text-sm min-h-[44px]
                transition-all disabled:opacity-50
                ${scheduledDate === getQuickDate(opt.days)
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-orange-400'
                }
              `}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <input
          type="date"
          value={scheduledDate}
          onChange={(e) => setScheduledDate(e.target.value)}
          min={getMinDate()}
          disabled={submitting}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm
                     focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-h-[48px]"
        />
      </div>

      {/* Time Selection */}
      {scheduledDate && (
        <div className="mb-6">
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Choose Time
          </label>
          <div className="grid grid-cols-4 gap-2">
            {timeSlots.map((slot) => (
              <button
                key={slot.value}
                onClick={() => setScheduledTime(slot.value)}
                disabled={submitting}
                className={`
                  px-2 py-3 rounded-xl font-medium text-xs min-h-[48px]
                  transition-all disabled:opacity-50
                  ${scheduledTime === slot.value
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-white border border-gray-300 text-gray-700 hover:border-orange-400'
                  }
                `}
              >
                {slot.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onClose}
          disabled={submitting}
          className="flex-1 min-h-[48px] px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium
                     hover:bg-gray-50 active:bg-gray-100 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting || !scheduledDate || !scheduledTime}
          className="flex-1 min-h-[48px] px-4 py-3 bg-orange-600 text-white rounded-xl font-medium
                     hover:bg-orange-700 active:bg-orange-800 transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Set Schedule
        </button>
      </div>
    </div>
  );
}

// Assign Draughter Modal Component - Supports multiple draughter selection
function AssignDraughterModal({ measurement, draughters, onAssign, onClose, formatDateTime }) {
  // Get currently assigned draughter IDs from the draughters array
  const currentDraughterIds = (measurement.draughters || [])
    .filter(d => d.isActive !== false)
    .map(d => d.draughterId || d.draughter?.id);

  // Fallback to legacy single draughter if no draughters array
  const initialSelected = currentDraughterIds.length > 0
    ? currentDraughterIds
    : (measurement.assignedDraughterId ? [measurement.assignedDraughterId] : []);

  const [selectedDraughters, setSelectedDraughters] = useState(initialSelected);

  const handleToggleDraughter = (draughterId) => {
    setSelectedDraughters(prev => {
      if (prev.includes(draughterId)) {
        return prev.filter(id => id !== draughterId);
      } else {
        return [...prev, draughterId];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedDraughters.length === 0) {
      toast.error('Please select at least one draughter');
      return;
    }
    onAssign(measurement.id, selectedDraughters);
  };

  const hasExistingAssignment = currentDraughterIds.length > 0 || measurement.assignedDraughterId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold">
            {hasExistingAssignment ? 'Reassign' : 'Assign'} Site Draughter(s)
          </h3>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="mb-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Project:</span>{' '}
              <span className="font-semibold text-gray-900">
                {measurement.designProject?.projectNumber}
              </span>
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Client:</span>{' '}
              {measurement.designProject?.salesForm?.clientName}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Location:</span>{' '}
              {measurement.location}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Scheduled:</span>{' '}
              <span className="font-semibold text-gray-900">
                {formatDateTime(measurement.scheduledDate)}
              </span>
            </p>
          </div>

          {hasExistingAssignment && (
            <div className="mb-4 bg-yellow-50 rounded-lg p-3 border border-yellow-200">
              <p className="text-sm text-yellow-800">
                Currently assigned to:{' '}
                <span className="font-semibold">
                  {(measurement.draughters || [])
                    .filter(d => d.isActive !== false)
                    .map(d => d.draughter?.nickname)
                    .filter(Boolean)
                    .join(', ') || measurement.draughter?.nickname || 'Unknown'}
                </span>
                {measurement.isSelfAssigned && ' (Self-assigned)'}
              </p>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Draughter(s) * <span className="text-gray-500 font-normal">(Select one or more)</span>
            </label>
            <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
              {draughters.map((draughter) => (
                <label
                  key={draughter.id}
                  className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                    selectedDraughters.includes(draughter.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedDraughters.includes(draughter.id)}
                    onChange={() => handleToggleDraughter(draughter.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-900">
                    {draughter.nickname} <span className="text-gray-500">- {draughter.fullName}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {selectedDraughters.length} selected · {draughters.length} site draughters available
            </p>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={selectedDraughters.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {hasExistingAssignment ? 'Reassign' : 'Assign'} {selectedDraughters.length > 1 ? 'Draughters' : 'Draughter'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Reschedule Measurement Modal Component
function RescheduleMeasurementModal({ measurement, onReschedule, onClose, formatDateTime }) {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pre-populate with current scheduled date/time
  useEffect(() => {
    if (measurement.scheduledDate) {
      const date = new Date(measurement.scheduledDate);
      setNewDate(date.toISOString().split('T')[0]);
      setNewTime(date.toTimeString().slice(0, 5));
    }
  }, [measurement]);

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get date string for X days from now
  const getQuickDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0];
  };

  // Format selected date for display
  const formatSelectedDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr + 'T00:00:00');
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Generate time slots in 30-minute intervals from 9 AM to 6 PM
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 18 && minute > 0) break;
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const label = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ label, value: time24 });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Format date/time preview
  const getDateTimePreview = () => {
    if (!newDate || !newTime) return null;
    try {
      const dateTime = new Date(`${newDate}T${newTime}`);
      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return dateTime.toLocaleDateString('en-US', options);
    } catch (e) {
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!newDate || !newTime) {
      toast.error('Please select both date and time');
      return;
    }

    setSubmitting(true);
    try {
      const scheduledDateTime = new Date(`${newDate}T${newTime}`);
      await onReschedule(measurement.id, scheduledDateTime.toISOString());
    } finally {
      setSubmitting(false);
    }
  };

  const previewDateTime = getDateTimePreview();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-orange-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <h3 className="text-lg font-semibold">Reschedule Measurement</h3>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-white hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Project Info */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Project:</span>{' '}
              <span className="font-semibold text-gray-900">
                {measurement.designProject?.projectNumber}
              </span>
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Client:</span>{' '}
              {measurement.designProject?.salesForm?.clientName}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Current Schedule:</span>{' '}
              <span className="font-semibold text-orange-600">
                {formatDateTime(measurement.scheduledDate)}
              </span>
            </p>
          </div>

          {/* Step 1: Date Selection */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Step 1: Choose New Date</p>

            {/* Quick Date Buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                { days: 0, label: 'Today' },
                { days: 1, label: 'Tomorrow' },
                { days: 2, label: 'In 2 Days' },
                { days: 3, label: 'In 3 Days' },
                { days: 7, label: 'Next Week' }
              ].map((opt) => (
                <button
                  key={opt.days}
                  type="button"
                  onClick={() => setNewDate(getQuickDate(opt.days))}
                  disabled={submitting}
                  className={`px-3 py-2 border-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 ${
                    newDate === getQuickDate(opt.days)
                      ? 'bg-orange-600 border-orange-600 text-white shadow-md'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Date Input */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">Or pick:</span>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={getMinDate()}
                disabled={submitting}
                className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 font-medium text-gray-900 text-sm"
              />
            </div>

            {/* Selected Date Display */}
            {newDate && (
              <div className="flex items-center gap-2 text-orange-700 bg-orange-100 px-3 py-2 rounded-lg">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium">{formatSelectedDate(newDate)}</span>
              </div>
            )}
          </div>

          {/* Step 2: Time Selection - Only show after date is selected */}
          {newDate && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Step 2: Choose New Time</p>

              {/* Time Grid - 30 minute intervals */}
              <div className="grid grid-cols-4 gap-2">
                {timeSlots.map((slot) => (
                  <button
                    key={slot.value}
                    type="button"
                    onClick={() => setNewTime(slot.value)}
                    disabled={submitting}
                    className={`px-2 py-2.5 border-2 rounded-lg font-medium text-xs transition-all disabled:opacity-50 ${
                      newTime === slot.value
                        ? 'bg-orange-600 border-orange-600 text-white shadow-md'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* DateTime Preview - Final confirmation */}
          {previewDateTime && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-xs font-medium text-green-700 mb-1">New Schedule:</p>
                  <p className="text-base font-bold text-green-900">{previewDateTime}</p>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !newDate || !newTime}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Rescheduling...
                </>
              ) : (
                'Reschedule'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Set Schedule Modal Component (for pending_schedule measurements)
function SetScheduleModal({ measurement, onSetSchedule, onClose }) {
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get date string for X days from now
  const getQuickDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toISOString().split('T')[0];
  };

  // Format selected date for display
  const formatSelectedDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr + 'T00:00:00');
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Generate time slots in 30-minute intervals from 9 AM to 6 PM
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === 18 && minute > 0) break;
        const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const label = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ label, value: time24 });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Format date/time preview
  const getDateTimePreview = () => {
    if (!scheduledDate || !scheduledTime) return null;
    try {
      const dateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return dateTime.toLocaleDateString('en-US', options);
    } catch (e) {
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!scheduledDate || !scheduledTime) {
      toast.error('Please select both date and time');
      return;
    }

    setSubmitting(true);
    try {
      const dateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      await onSetSchedule(measurement.id, dateTime.toISOString());
    } finally {
      setSubmitting(false);
    }
  };

  const previewDateTime = getDateTimePreview();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-orange-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <h3 className="text-lg font-semibold">Set Measurement Schedule</h3>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-white hover:text-gray-200 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Project Info */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Project:</span>{' '}
              <span className="font-semibold text-gray-900">
                {measurement.designProject?.projectNumber}
              </span>
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <span className="font-medium">Client:</span>{' '}
              {measurement.designProject?.salesForm?.clientName}
            </p>
            <p className="text-sm text-gray-600">
              <span className="font-medium">Location:</span>{' '}
              {measurement.location || 'N/A'}
            </p>
          </div>

          {/* Step 1: Date Selection */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Step 1: Choose Date</p>

            {/* Quick Date Buttons */}
            <div className="flex flex-wrap gap-2">
              {[
                { days: 0, label: 'Today' },
                { days: 1, label: 'Tomorrow' },
                { days: 2, label: 'In 2 Days' },
                { days: 3, label: 'In 3 Days' },
                { days: 7, label: 'Next Week' }
              ].map((opt) => (
                <button
                  key={opt.days}
                  type="button"
                  onClick={() => setScheduledDate(getQuickDate(opt.days))}
                  disabled={submitting}
                  className={`px-3 py-2 border-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50 ${
                    scheduledDate === getQuickDate(opt.days)
                      ? 'bg-orange-600 border-orange-600 text-white shadow-md'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Date Input */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">Or pick:</span>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={getMinDate()}
                disabled={submitting}
                className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:opacity-50 font-medium text-gray-900 text-sm"
              />
            </div>

            {/* Selected Date Display */}
            {scheduledDate && (
              <div className="flex items-center gap-2 text-orange-700 bg-orange-100 px-3 py-2 rounded-lg">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium">{formatSelectedDate(scheduledDate)}</span>
              </div>
            )}
          </div>

          {/* Step 2: Time Selection - Only show after date is selected */}
          {scheduledDate && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Step 2: Choose Time</p>

              {/* Time Grid - 30 minute intervals */}
              <div className="grid grid-cols-4 gap-2">
                {timeSlots.map((slot) => (
                  <button
                    key={slot.value}
                    type="button"
                    onClick={() => setScheduledTime(slot.value)}
                    disabled={submitting}
                    className={`px-2 py-2.5 border-2 rounded-lg font-medium text-xs transition-all disabled:opacity-50 ${
                      scheduledTime === slot.value
                        ? 'bg-orange-600 border-orange-600 text-white shadow-md'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* DateTime Preview - Final confirmation */}
          {previewDateTime && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-xs font-medium text-green-700 mb-1">Scheduled For:</p>
                  <p className="text-base font-bold text-green-900">{previewDateTime}</p>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !scheduledDate || !scheduledTime}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Setting...
                </>
              ) : (
                'Set Schedule'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Date Range Calendar Component
function DateRangeCalendar({ dateFrom, dateTo, onSelect, onClear, onClose }) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (dateFrom) return new Date(dateFrom);
    return new Date();
  });
  const [selecting, setSelecting] = useState(dateFrom && !dateTo ? 'end' : 'start');
  const [hoverDate, setHoverDate] = useState(null);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const formatDateStr = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const handleDateClick = (date) => {
    if (!date) return;
    const dateStr = formatDateStr(date);

    if (selecting === 'start') {
      onSelect(dateStr, '');
      setSelecting('end');
    } else {
      // If clicking before the start date, swap them
      if (dateFrom && dateStr < dateFrom) {
        onSelect(dateStr, dateFrom);
      } else {
        onSelect(dateFrom, dateStr);
      }
      setSelecting('start');
    }
  };

  const isInRange = (date) => {
    if (!date || !dateFrom) return false;
    const dateStr = formatDateStr(date);
    const endDate = dateTo || (hoverDate && selecting === 'end' ? hoverDate : null);
    if (!endDate) return false;

    const start = dateFrom < endDate ? dateFrom : endDate;
    const end = dateFrom < endDate ? endDate : dateFrom;
    return dateStr >= start && dateStr <= end;
  };

  const isStartDate = (date) => {
    if (!date || !dateFrom) return false;
    return formatDateStr(date) === dateFrom;
  };

  const isEndDate = (date) => {
    if (!date || !dateTo) return false;
    return formatDateStr(date) === dateTo;
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 w-72">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={prevMonth}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="font-medium text-gray-900">
          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button
          onClick={nextMonth}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Instruction */}
      <div className="text-xs text-center text-gray-500 mb-2">
        {selecting === 'start' ? 'Select start date' : 'Select end date'}
      </div>

      {/* Week days header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekDays.map(day => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="p-2" />;
          }

          const isStart = isStartDate(date);
          const isEnd = isEndDate(date);
          const inRange = isInRange(date);
          const isToday = formatDateStr(date) === formatDateStr(new Date());

          return (
            <button
              key={formatDateStr(date)}
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => selecting === 'end' && setHoverDate(formatDateStr(date))}
              onMouseLeave={() => setHoverDate(null)}
              className={`p-2 text-sm rounded-lg transition-colors ${
                isStart || isEnd
                  ? 'bg-blue-600 text-white font-medium'
                  : inRange
                    ? 'bg-blue-100 text-blue-800'
                    : isToday
                      ? 'bg-gray-100 font-medium'
                      : 'hover:bg-gray-100'
              }`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Selected range display */}
      {(dateFrom || dateTo) && (
        <div className="mt-3 pt-3 border-t text-xs text-gray-600 text-center">
          {dateFrom && (
            <span className="font-medium">{new Date(dateFrom).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          )}
          {dateFrom && dateTo && <span className="mx-2">→</span>}
          {dateTo && (
            <span className="font-medium">{new Date(dateTo).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between mt-3 pt-3 border-t">
        <button
          onClick={() => { onClear(); setSelecting('start'); }}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Clear
        </button>
        <button
          onClick={onClose}
          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          Done
        </button>
      </div>
    </div>
  );
}
